/**
 * UnifiedFeed — vertical timeline with step dots, collapsible agent messages,
 * and tool call cards. Single view replacing separate tabs.
 */

import { useRef, useEffect } from "react";
import { ScrollArea } from "@/ui/primitives/scroll-area";

import { Brain, Loader2, Activity, Filter } from "lucide-react";
import { cn } from "@/utils/misc";
import type { TimelineProps } from "./timeline-types";
import { useTimelineData } from "./useTimelineData";
import {
  TimelineActivityEntry,
  TimelineLifecycleEntry,
  TimelineMessageGroup,
  TimelineToolCallEntry,
} from "./TimelineEntries";

export function UnifiedFeed({
  task,
  subAgents,
  taskTree,
  isRunning,
}: TimelineProps) {
  const { groupedItems, availableSources, hiddenSources, toggleSource } =
    useTimelineData(task, subAgents, taskTree);

  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isRunning || !scrollRef.current) return;
    // Radix ScrollArea root wraps the actual scroll viewport; find it so we can
    // scroll only the inner container without propagating to ancestor scroll
    // containers (the page/window). Using `Element.scrollIntoView()` here would
    // bubble up and scroll the parent route, leaving the sticky tab header
    // pinned with the content stranded at the bottom of the viewport.
    const viewport = scrollRef.current.querySelector<HTMLDivElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!viewport) return;
    const isNearBottom =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 200;
    if (isNearBottom) {
      viewport.scrollTo({ top: viewport.scrollHeight, behavior: "smooth" });
    }
  }, [groupedItems.length, isRunning]);

  if (groupedItems.length === 0) {
    if (isRunning) {
      return (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Waiting for events...
        </div>
      );
    }
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        No events recorded.
      </p>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden">
      {/* Source filters */}
      {availableSources.length > 2 && (
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          <Filter className="h-3 w-3 shrink-0 text-muted-foreground/40" />
          {availableSources.map((src) => (
            <button
              key={src.id}
              type="button"
              onClick={() => toggleSource(src.id)}
              className={cn(
                "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-medium transition-colors",
                hiddenSources.has(src.id)
                  ? "border-transparent bg-muted/20 text-muted-foreground/30 line-through"
                  : "border-border bg-background text-muted-foreground hover:bg-muted/30",
              )}
            >
              {src.id === "main" ? (
                <Brain className="h-2.5 w-2.5" />
              ) : src.id === "activity" ? (
                <Activity className="h-2.5 w-2.5" />
              ) : (
                <Brain className="h-2.5 w-2.5" />
              )}
              {src.label}
            </button>
          ))}
        </div>
      )}

      {/* Steps — `isolate` creates a stacking context so step-row dots can
          never paint over the page header above this scroll viewport. */}
      <ScrollArea className="flex-1" ref={scrollRef}>
        <div className="isolate pl-1 pr-4">
          {groupedItems.map((item, idx) => {
            const isLast = idx === groupedItems.length - 1 && !isRunning;

            if (item.kind === "single") {
              if (item.entry.type === "activity") {
                return (
                  <TimelineActivityEntry
                    key={item.entry.key}
                    entry={item.entry}
                    isLast={isLast}
                  />
                );
              }
              if (item.entry.type === "lifecycle") {
                return (
                  <TimelineLifecycleEntry
                    key={item.entry.key}
                    entry={item.entry}
                    isLast={isLast}
                  />
                );
              }
              return null;
            }

            if (item.kind === "tool_pair") {
              return (
                <TimelineToolCallEntry
                  key={item.key}
                  pair={item}
                  isLast={isLast}
                />
              );
            }

            return (
              <TimelineMessageGroup
                key={item.key}
                group={item}
                isLast={isLast}
              />
            );
          })}

          {isRunning && (
            <div className="relative flex gap-3">
              <div className="relative flex h-[23px] w-[23px] shrink-0 items-center justify-center rounded-full border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40">
                <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
              </div>
              <div className="flex-1 pb-4 pt-0.5">
                <span className="text-xs text-blue-600 dark:text-blue-400">
                  Agent is working...
                </span>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>
    </div>
  );
}
